import React, { useContext } from 'react';
import { Tooltip } from '@mantine/core';
import MyUserContext from './../myUserContext.js';

const PlatformRatings = ({ art, fullMode = false, center = true, twoColumns = false, showVotes = false, hidePairVotes = false }) => {
  const votesVisible = fullMode || showVotes;
  const myUser = useContext(MyUserContext);
  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const rawInfos = art.otherPlatformInfos || [];

  // Fold the separate "<x>User" entry into the paired critic entry so
  // both scores render under one icon (e.g. Metacritic "86/37 6.0/1028",
  // Rotten Tomatoes "60/47 92/1210"). If only one of the two exists, we
  // keep whichever it is with no visual change.
  const mergePair = (list, criticName, userName) => {
    const userInfo = list.find(i => i.name === userName);
    if (!userInfo) return list;
    const criticInfo = list.find(i => i.name === criticName);
    const merged = {
      name: criticName,
      link: (criticInfo && criticInfo.link) || userInfo.link,
      score: criticInfo ? criticInfo.score : undefined,
      votes: criticInfo ? criticInfo.votes : undefined,
      userScore: userInfo.score,
      userVotes: userInfo.votes,
    };
    return list
      .filter(i => i.name !== criticName && i.name !== userName)
      .concat(criticInfo || userInfo ? [merged] : []);
  };
  const otherPlatformInfos = mergePair(
    mergePair(rawInfos, 'metacritic', 'metacriticUser'),
    'rottenTomatoes',
    'rottenTomatoesAudience',
  );

  const formatRating = (rating) => (rating === 0 ? '-' : rating.toFixed(1));
  const formatMetascore = (rating) => (rating === 0 ? '-' : Math.round(rating).toString());
  const iconSizeClass = 
    fullMode
      ? "max-w-24 max-h-10"
      : "max-w-4 max-h-4 opacity-95";
  const textStyle = { color: 'var(--mantine-color-dimmed)' };
  const textClass =
    fullMode
      ? "text-sm font-medium leading-none"
      : "text-[11px] font-medium leading-none";

  const getIconForName = (name) => {
    switch (name.toLowerCase()) {
      case 'imdb': return '/imdb_small_icon.png';
      case 'kinopoisk': return '/kinopoisk_small_icon.png';
      case 'applebooks': return '/applebooks_icon.png';
      case 'steam': return '/steam_icon.png';
      case 'rottentomatoes': return '/rottentomatoes_icon.png';
      case 'reddit': return '/reddit_icon.png';
      case 'amazon': return '/amazon_icon.png';
      case 'goodreads': return '/goodreads_icon.png';
      case 'metacritic': return '/metacritic_icon.png';
      case 'wikipedia': return '/wiki_icon.png';
      case 'litres': return '/litres_icon.png';
      case 'livelib': return '/livelib_icon.png';
      default: return null;
    }
  };

  const renderIconOrText = (icon, fullMode) => {
    if (icon === '/rottentomatoes_icon.png') {
      return (
        <span className="font-bold" style={{ color: 'var(--mantine-color-red-text)' }}>
          {fullMode ? 'Rotten Tomatoes' : 'RT'}
        </span>
      );
    }
    const title = icon === '/metacritic_icon.png' ? 'Рейтинг платформы Metacritics' : '';
    const additionalClasses = icon === '/wiki_icon.png' ? 'rounded-md p-1' : '';
    const additionalStyle = icon === '/wiki_icon.png' ? { backgroundColor: 'var(--mantine-color-body)' } : {};
    return <img src={icon} title={title} className={`rounded-md ${iconSizeClass} ${additionalClasses}`} style={additionalStyle} />;
  };

  const renderItem = ({ name, link, score, votes, userScore, userVotes }) => {
    const icon = getIconForName(name);
    if (!icon) return null;
    // Wikipedia carries no rating — show it as a clickable icon on the
    // art page only (fullMode), and skip it entirely elsewhere so hover
    // overlays / detail panels / thumbnails stay rating-only.
    if (name === 'wikipedia') {
      if (!fullMode || !link) return null;
      return (
        <div key={name} className="flex items-center">
          <a href={link} target="_blank" rel="noopener noreferrer">
            {renderIconOrText(icon, fullMode)}
          </a>
        </div>
      );
    }
    // Apple Books / iTunes: never surface the icon. The rating is
    // unreliable so we can't show the number, and we don't want the
    // logo either.
    if (name === 'appleBooks') {
      return null;
    }
    // Litres: show the icon as a clickable link out, but never the
    // rating — the number they surface is meaningless.
    if (name === 'litres') {
      if (!fullMode || !link) return null;
      return (
        <div key={name} className="flex items-center">
          <a href={link} target="_blank" rel="noopener noreferrer">
            {renderIconOrText(icon, fullMode)}
          </a>
        </div>
      );
    }
    const hasCritic = score !== undefined && score !== 0;
    const hasUser = userScore !== undefined && userScore !== 0;
    // In detail-panel (showVotes) and fullMode we keep every entry with a
    // known icon and paper over missing values with '-', so the user can
    // see we've linked the art to a platform even before scores land.
    if (!fullMode && !showVotes && !hasCritic && !hasUser) return null;

    // Merged critic + audience/user view:
    //   Metacritic     "86/37 6.0/1028"     (Metascore int + user score 1dp)
    //   Rotten Tomatoes "60/47 92/1,210"    (both sides are integer %)
    // Each half is a tap/hover target labelling itself so users know
    // which score is which.
    const isPairPlatform = name === 'metacritic' || name === 'rottenTomatoes';
    // In showVotes / fullMode always render the two-half view for the
    // pair platforms — with '-' placeholders on whichever side lacks data.
    const isMerged = isPairPlatform && (votesVisible || hasUser || userScore !== undefined);
    if (isMerged) {
      const isRT = name === 'rottenTomatoes';
      // RT is displayed as a percentage on rottentomatoes.com — append '%'
      // to the score half so users recognize it. Metascore stays bare.
      const critFmt = isRT ? (v) => `${formatMetascore(v)}%` : formatMetascore;
      const userFmt = isRT ? (v) => `${formatMetascore(v)}%` : formatRating;
      const sep = ' / ';
      // Placeholder logic:
      //   compact (no votesVisible): only paper over votes when a score is
      //     present ("60/47" or "-/47"); if both missing, skip the half.
      //   votesVisible: always show "score/votes", with '-' for whichever
      //     is missing, so linkage is visible even before scrapes land.
      const critScoreStr = hasCritic ? critFmt(score) : '-';
      const critVotesStr = votes ? votes.toLocaleString() : '-';
      const userScoreStr = hasUser ? userFmt(userScore) : '-';
      const userVotesStr = userVotes ? userVotes.toLocaleString() : '-';
      const criticText = hidePairVotes
        ? (hasCritic ? critScoreStr : '')
        : votesVisible
          ? `${critScoreStr}${sep}${critVotesStr}`
          : (hasCritic || votes
              ? `${critScoreStr}${votes ? `${sep}${critVotesStr}` : ''}`
              : '');
      const userText = hidePairVotes
        ? (hasUser ? userScoreStr : '')
        : votesVisible
          ? `${userScoreStr}${sep}${userVotesStr}`
          : (hasUser || userVotes
              ? `${userScoreStr}${userVotes ? `${sep}${userVotesStr}` : ''}`
              : '');
      const criticLabel = language === 'ru' ? 'Рейтинг критиков' : "Critics' rating";
      const userLabel = language === 'ru'
        ? (name === 'rottenTomatoes' ? 'Рейтинг зрителей' : 'Рейтинг пользователей')
        : (name === 'rottenTomatoes' ? "Audience rating" : "Users' rating");
      // RT no longer needs the dotted underline — the '%' + tooltip
      // already read as interactive on desktop and mobile.
      const partClass = isRT
        ? `${textClass} cursor-pointer`
        : `${textClass} cursor-pointer underline decoration-dotted underline-offset-2`;
      return (
        <div key={name} className="flex items-center space-x-1">
          {link ? (
            <a href={link} target="_blank" rel="noopener noreferrer">
              {renderIconOrText(icon, fullMode)}
            </a>
          ) : (
            renderIconOrText(icon, fullMode)
          )}
          {criticText && (
            <Tooltip label={criticLabel} withArrow events={{ hover: true, focus: true, touch: true }}>
              <span className={partClass} style={textStyle} tabIndex={0}>{criticText}</span>
            </Tooltip>
          )}
          {userText && (
            <Tooltip label={userLabel} withArrow events={{ hover: true, focus: true, touch: true }}>
              <span className={partClass} style={textStyle} tabIndex={0}>{userText}</span>
            </Tooltip>
          )}
        </div>
      );
    }

    return (
      <div key={name} className="flex items-center space-x-0.5">
        {link ? (
          <a href={link} target="_blank" rel="noopener noreferrer">
            {renderIconOrText(icon, fullMode)}
          </a>
        ) : (
          renderIconOrText(icon, fullMode)
        )}
        {(() => {
          const hasScore = score !== undefined && (fullMode || score !== 0);
          const hasVoteInline = votesVisible && votes !== undefined && votes !== 0;
          // In compact non-showVotes mode we drop the whole span when
          // neither score nor votes is present. In showVotes/fullMode we
          // always render "score / votes", padding with '-' where needed
          // so linkage stays visible even before scrapes land.
          if (!votesVisible && !hasScore && !hasVoteInline) return null;
          const scoreText = hasScore
            ? (name === 'metacritic' ? formatMetascore(score) : formatRating(score))
            : '-';
          const showVotesPart = votesVisible || hasVoteInline;
          const votesText = hasVoteInline
            ? votes.toLocaleString()
            : (votesVisible ? '-' : '');
          return (
            <span className={textClass} style={textStyle}>
              {scoreText}{showVotesPart ? ` / ${votesText}` : ''}
            </span>
          );
        })()}
      </div>
    );
  };

  if (twoColumns) {
    const visibleItems = otherPlatformInfos.filter(({ name, score, userScore }) => {
      if (!getIconForName(name)) return false;
      const hasAnyScore = (score !== undefined && score !== 0) || (userScore !== undefined && userScore !== 0);
      if (!fullMode && !hasAnyScore) return false;
      return true;
    });
    const rows = [];
    for (let i = 0; i < visibleItems.length; i += 2) {
      rows.push(visibleItems.slice(i, i + 2));
    }
    return (
      <div className="flex flex-col gap-0.5 text-xs font-medium leading-none opacity-95 w-fit">
        {rows.map((row, idx) => (
          <div key={idx} className="flex items-center gap-1">
            {row.map(renderItem)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-1 text-xs font-medium leading-none opacity-95${center ? ' justify-center' : ''}`}>
      {otherPlatformInfos.map(renderItem)}
    </div>
  );
};

export default PlatformRatings;
